/* eslint-disable @typescript-eslint/no-explicit-any */
import PlayoffBadge from "../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/PlayoffBadge";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { pad2, useCountdown, type Countdown } from "./useCountdown";
import { buildAllVms, dayLabelOf, startsIn, type MatchVm, type TeamVm } from "./matchVm";
import type { MatchBucket } from "./useTournamentMatches";
import { IcArr, IcCal, IcCalL, IcChk, IcDn, IcGrid, IcList, IcListL, IcLive, IcPin, IcPlay, IcSearch, IcSrchL, IcTeams, IcTrophy } from "./icons";
import { getPlayerImageUrl } from "../../helper/helper";

type Props = { live: any[]; up: any[]; past: any[]; loading: boolean; tournamentName: string; tournamentEnded: boolean; tournamentStart?: string | null; onSchedule?: () => void };
const BUCKETS: [MatchBucket, string][] = [["live", "Live"], ["up", "Upcoming"], ["past", "Past"]];

const Crest = ({ t, size }: { t: TeamVm; size?: number }) => (
  <span className="cr" style={{ background: t.color, width: size, height: size, fontSize: size ? Math.max(7, size / 3.4) : undefined, overflow: "hidden" }}>
    {t.image ? <img src={getPlayerImageUrl(t.image) ?? ""} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.currentTarget.style.display = "none"; }} /> : t.code}
  </span>
);

const Score = ({ t, dnb }: { t: TeamVm; dnb: string }) =>
  !t.score ? <span className="dnb">{dnb}</span> : <><b>{t.score}</b>{t.overs && <small>{t.overs} ov</small>}</>;

const TeamRow = ({ t, m, list }: { t: TeamVm; m: MatchVm; list?: boolean }) => {
  const past = m.bucket === "past";
  const outcome = m.outcome === "NR" ? "NR" : m.outcome === "T" ? "T" : t.won ? "W" : t.lost ? "L" : null;
  return (
    <div className={`tr ${past && t.lost ? "l" : ""}`}>
      <Crest t={t} size={list ? 22 : undefined} />
      <div className="nm"><span>{t.name}</span>{!list && past && t.won && <span className="w" title="Won"><IcChk /></span>}{m.bucket === "live" && t.batting && <span className="bat">BATTING</span>}</div>
      <div className="sc">{m.bucket === "up" ? null : <Score t={t} dnb={m.bucket === "live" ? "Yet to bat" : "Did not bat"} />}</div>
      {list && (past && outcome ? <span className={`wl ${outcome}`}>{outcome}</span> : <span />)}
    </div>
  );
};

const PotmBlock = ({ m }: { m: MatchVm }) => m.potm ? (
  <div className="pm"><span className="av" style={{ background: "#EEEEF7", color: "#29276B" }}>{m.potm.split(" ").map((w) => w[0]).join("").slice(0, 2)}</span><span><span className="k">PLAYER OF THE MATCH</span><span className="v">{m.potm}</span></span></div>
) : <span className="ft-note">No player of the match</span>;

export const CountdownBox = ({ cd }: { cd: Countdown }) =>
  cd.over ? (
    <div className="mc-res cdbox soon"><span className="cdl">Starting now</span></div>
  ) : (
    <div className={`mc-res cdbox${cd.soon ? " soon" : ""}`} role="timer" aria-label={`Starts in ${cd.h} hours ${cd.m} minutes ${cd.s} seconds`}>
      <span className="cdl">Starts in</span>
      <span className="cdv">
        <span className="u"><b>{pad2(cd.h)}</b><small>hrs</small></span><i>:</i>
        <span className="u"><b>{pad2(cd.m)}</b><small>min</small></span><i>:</i>
        <span className="u"><b>{pad2(cd.s)}</b><small>sec</small></span>
      </span>
    </div>
  );

/** WON / LOST / TIED / NO RESULT for a finished match, seen from one team's side. */
export const resultTagFor = (m: MatchVm, teamName: string): string => {
  if (m.outcome === "NR") return "NO RESULT";
  if (m.outcome === "T") return "TIED";
  const mine = [m.a, m.b].find((t) => t.name.trim().toLowerCase() === teamName.trim().toLowerCase());
  return mine?.won ? "WON" : mine?.lost ? "LOST" : "RESULT";
};

export const MatchCard = ({ m, onOpen, viewAs }: { m: MatchVm; onOpen: (m: MatchVm) => void; viewAs?: string }) => {
  const U = m.bucket === "up" ? startsIn(m.start) : null;
  const cd = useCountdown(m.start, m.bucket === "up");
  return (
    <a className="mc" href={`/matches-details/${m.id}`} onClick={(e) => { e.preventDefault(); onOpen(m); }} style={{ ["--ca" as any]: m.a.color, ["--cb" as any]: m.b.color }} aria-label={`${m.a.name} versus ${m.b.name}`}>
      <div className="mc-top">{m.label ? <PlayoffBadge playoffType={m.raw.playoffType} /> : <span className="no">MATCH {m.no}</span>}<span className="dot" /><span>{m.bucket === "past" && m.start ? m.start.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) + ", " : ""}{m.time}</span>
        {m.ground && <span className="gr"><IcPin />{m.ground}</span>}
        {m.bucket === "live" ? <span className="tag live"><span className="ldot on" />LIVE</span> : <span className="tag">{m.bucket === "past" ? (viewAs ? resultTagFor(m, viewAs) : "RESULT") : (U?.when ?? "").toUpperCase()}</span>}
      </div>
      <div className="mc-teams"><TeamRow t={m.a} m={m} /><TeamRow t={m.b} m={m} /></div>
      {m.bucket === "past" && <div className={`mc-res ${m.outcome === "W" ? "" : "nr"}`}>{m.resultTitle}</div>}
      {m.bucket === "live" && <div className="mc-res lv">{m.resultTitle && m.resultTitle !== "-" ? m.resultTitle : "Match in progress"}</div>}
      {m.bucket === "up" && (cd ? <CountdownBox cd={cd} /> : <div className="mc-res up">{U?.text}</div>)}
      <div className="mc-ft">
        {m.bucket === "past" && <PotmBlock m={m} />}
        {m.bucket === "live" && (m.hasLiveStream ? <span className="rem"><IcPlay />Watch live</span> : <span className="ft-note">Ball-by-ball updates</span>)}
        {m.bucket === "up" && <span className="ft-note">{m.raw.tossResult ? m.raw.tossResult : "Toss and squads at match start"}</span>}
        <span className="go">{m.bucket === "past" ? "Match centre" : m.bucket === "live" ? "Live match" : "Match preview"}<IcArr /></span>
      </div>
    </a>
  );
};

export const MatchRowItem = ({ m, onOpen }: { m: MatchVm; onOpen: (m: MatchVm) => void }) => {
  const U = m.bucket === "up" ? startsIn(m.start) : null;
  const cd = useCountdown(m.start, m.bucket === "up");
  return (
    <a className="row" href={`/matches-details/${m.id}`} onClick={(e) => { e.preventDefault(); onOpen(m); }}>
      <div className="when">{m.label ? <PlayoffBadge playoffType={m.raw.playoffType} /> : <b>Match {m.no}</b>}<span>{m.time}</span></div>
      <div className="tms"><TeamRow t={m.a} m={m} list /><TeamRow t={m.b} m={m} list /></div>
      {m.bucket === "past" ? <div className="marg"><b>{m.marginTitle}</b></div> : m.bucket === "live" ? <div className="res lv">{m.resultTitle}</div> : <div className="res nr">{cd ? (cd.over ? "Starting now" : <span className={`cdrow${cd.soon ? " soon" : ""}`} role="timer"><small>Starts in</small><b>{pad2(cd.h)}:{pad2(cd.m)}:{pad2(cd.s)}</b></span>) : U?.text}</div>}
      <div>{m.bucket === "past" ? <PotmBlock m={m} /> : m.bucket === "live" ? <span className="tag live"><span className="ldot on" />LIVE</span> : <span className="ft-note">{U?.when}</span>}</div>
      <div className="gr">{m.ground}</div>
      <span className="go">{m.bucket === "live" ? "Live" : m.bucket === "up" ? "Preview" : "Open"}<IcArr /></span>
    </a>
  );
};

type Range = { from: string; to: string } | null;
const parseKey = (k: string) => { const [y, m, d] = k.split("-").map(Number); return new Date(y, m - 1, d); };
const keyOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const Calendar = ({ days, counts, range, onPick, onClose }: { days: string[]; counts: Record<string, number>; range: Range; onPick: (r: Range) => void; onClose: () => void }) => {
  const first = days[0] ? parseKey(days[0]) : new Date();
  const [month, setMonth] = useState(new Date(first.getFullYear(), first.getMonth(), 1));
  const [anchor, setAnchor] = useState<string | null>(null);
  const todayKey = keyOf(new Date());
  const set = new Set(days);
  const lead = (new Date(month.getFullYear(), month.getMonth(), 1).getDay() + 6) % 7;
  const dim = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const a = anchor ?? range?.from, b = anchor ?? range?.to;
  const preset = (k: string) => {
    const t = new Date(); const add = (n: number) => keyOf(new Date(t.getFullYear(), t.getMonth(), t.getDate() + n));
    if (k === "all") onPick(null);
    else if (k === "today") onPick({ from: add(0), to: add(0) });
    else if (k === "tom") onPick({ from: add(1), to: add(1) });
    else if (k === "yes") onPick({ from: add(-1), to: add(-1) });
    else { const wk = days.filter((d) => [0, 6].includes(parseKey(d).getDay())); if (wk.length) onPick({ from: wk[0], to: wk[wk.length - 1] }); }
    onClose();
  };
  const t = new Date();
  const k = (n: number) => keyOf(new Date(t.getFullYear(), t.getMonth(), t.getDate() + n));
  const has = (key: string) => set.has(key);
  const presets: [string, string, boolean][] = [["all", "All dates", true], ["today", "Today", has(k(0))], ["tom", "Tomorrow", has(k(1))], ["yes", "Yesterday", has(k(-1))], ["wk", "Weekend", days.some((d) => [0, 6].includes(parseKey(d).getDay()))]];
  const cells: ReactNode[] = [];
  for (let i = 0; i < lead; i++) cells.push(<span key={`l${i}`} />);
  for (let d = 1; d <= dim; d++) {
    const key = keyOf(new Date(month.getFullYear(), month.getMonth(), d));
    if (!set.has(key)) { cells.push(<span key={key}>{d}</span>); continue; }
    const cls = [a && key === a ? "s" : "", b && key === b ? "e" : "", a && b && key > a && key < b ? "in" : "", key === todayKey ? "today" : ""].join(" ");
    cells.push(<button key={key} className={cls} title={`${counts[key] ?? 0} matches`} onClick={() => { if (!anchor) setAnchor(key); else { const [lo, hi] = key < anchor ? [key, anchor] : [anchor, key]; onPick({ from: lo, to: hi }); onClose(); } }}>{d}<i /></button>);
  }
  return (
    <div className="cal" role="dialog" aria-label="Filter by date">
      <div className="pre">{presets.map(([key, label, ok]) => (<button key={key} disabled={!ok} className={(key === "all" && !range) ? "on" : ""} onClick={() => preset(key)}>{label}</button>))}</div>
      <div className="mh"><button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label="Previous month">‹</button>{month.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}<button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label="Next month">›</button></div>
      <div className="wk">{["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <span key={i}>{d}</span>)}</div>
      <div className="dd2">{cells}</div>
      <div className="ft"><span>{anchor ? "Pick the last day, or click the same day again" : "Pick a day or a range. Dots are match days."}</span><div className="r">{range && <button className="btn" onClick={() => { onPick(null); onClose(); }}>Clear</button>}</div></div>
    </div>
  );
};

export const Empty = ({ icon, title, text, acts, meta }: { icon: ReactNode; title: string; text: string; acts?: ReactNode; meta?: string }) => (
  <div className="empty"><span className="ic">{icon}</span><h3>{title}</h3><p>{text}</p>{meta && <span className="meta2">{meta}</span>}{acts && <div className="acts">{acts}</div>}</div>
);

/** Shown only to people who can schedule matches in this league (the old tab's Schedule button, as a card). */
export const ScheduleCard = ({ onSchedule, tournamentName }: { onSchedule: () => void; tournamentName: string }) => (
  <button type="button" className="schedcard" onClick={onSchedule}>
    <span className="plus" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg></span>
    <span className="tx"><b>Schedule a match</b><span>Add a fixture to {tournamentName}: pick the teams, date, time and ground.</span></span>
    <span className="go">Schedule<IcArr /></span>
  </button>
);

const MatchesPanel = ({ live, up, past, loading, tournamentName, tournamentEnded, tournamentStart, onSchedule }: Props) => {
  const nav = useNavigate();
  const all = useMemo(() => buildAllVms(live, up, past), [live, up, past]);
  const by = useMemo(() => ({ live: all.filter((m) => m.bucket === "live"), up: all.filter((m) => m.bucket === "up"), past: all.filter((m) => m.bucket === "past") }), [all]);
  const [tab, setTab] = useState<MatchBucket | null>(null);
  const [view, setView] = useState<"grid" | "list">("grid");
  const [team, setTeam] = useState("all");
  const [q, setQ] = useState("");
  const [range, setRange] = useState<Range>(null);
  const [teamOpen, setTeamOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const dd = useRef<HTMLDivElement>(null);

  const active: MatchBucket = tab ?? (by.live.length ? "live" : by.up.length && !tournamentEnded ? "up" : by.past.length ? "past" : by.up.length ? "up" : "live");
  useEffect(() => { setTeam("all"); setRange(null); }, [active]);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (!dd.current?.contains(e.target as Node)) { setTeamOpen(false); setCalOpen(false); } };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const inTab = by[active];
  const teams = useMemo(() => { const m = new Map<string, TeamVm>(); inTab.forEach((x) => { [x.a, x.b].forEach((t) => m.set(t.name, t)); }); return [...m.values()].sort((x, y) => x.name.localeCompare(y.name)); }, [inTab]);
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return inTab
      .filter((m) => team === "all" || m.a.name === team || m.b.name === team)
      .filter((m) => !range || (m.dayKey && m.dayKey >= range.from && m.dayKey <= range.to))
      .filter((m) => !ql || [m.a.name, m.b.name, m.a.code, m.b.code, m.ground, m.no ? `match ${m.no}` : "", m.potm ?? "", m.label].join(" ").toLowerCase().includes(ql));
  }, [inTab, team, range, q]);
  const days = useMemo(() => [...new Set(inTab.map((m) => m.dayKey).filter(Boolean))].sort(), [inTab]);
  const dayCounts = useMemo(() => inTab.reduce<Record<string, number>>((a, m) => { a[m.dayKey] = (a[m.dayKey] ?? 0) + 1; return a; }, {}), [inTab]);
  const grouped = useMemo(() => {
    const g = new Map<string, MatchVm[]>();
    filtered.forEach((m) => { const k = m.dayKey || "tba"; g.set(k, [...(g.get(k) ?? []), m]); });
    const keys = [...g.keys()].sort((x, y) => (active === "past" ? y.localeCompare(x) : x.localeCompare(y)));
    return keys.map((k) => ({ k, list: g.get(k)!.sort((x, y) => (active === "past" ? y.ms - x.ms : x.ms - y.ms)) }));
  }, [filtered, active]);

  const anyFilter = team !== "all" || !!q || !!range;
  const clear = () => { setTeam("all"); setQ(""); setRange(null); };
  const open = (m: MatchVm) => nav(`/matches-details/${m.id}`);
  const rangeLabel = !range ? "All dates" : range.from === range.to ? parseKey(range.from).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : `${parseKey(range.from).getDate()}–${parseKey(range.to).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
  const total = all.length;
  const todayKey = keyOf(new Date());
  const nextUp = by.up.slice().sort((x, y) => x.ms - y.ms)[0];

  if (!loading && total === 0)
    return (<main className="wrap main">{onSchedule && <ScheduleCard onSchedule={onSchedule} tournamentName={tournamentName} />}<div className="empty page"><span className="ic"><IcCalL /></span><h3>Fixtures aren't out yet</h3><p>The organiser hasn't published the match schedule for {tournamentName}.</p>{tournamentStart && <span className="meta2">Tournament starts {new Date(tournamentStart).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>}</div></main>);

  const emptyTab = (() => {
    if (anyFilter) return <Empty icon={<IcSrchL />} title={`No ${{ live: "live", up: "upcoming", past: "past" }[active]} matches for these filters`} text="Try another team or date, or clear the filters." acts={<button className="btn primary" onClick={clear}>Clear filters</button>} />;
    if (active === "live") return nextUp
      ? <Empty icon={<IcLive />} title="No match is live right now" text="Live scores show here from the toss." meta={`Next: ${nextUp.a.name} v ${nextUp.b.name} · ${startsIn(nextUp.start).when}, ${nextUp.time}`} acts={<><button className="btn primary" onClick={() => setTab("up")}>See upcoming</button>{by.past.length > 0 && <button className="btn" onClick={() => setTab("past")}>See results</button>}</>} />
      : <Empty icon={<IcTrophy />} title="No live matches" text={tournamentEnded ? `${tournamentName} has finished.` : "Live scores show here once a match begins."} meta={`${by.past.length} of ${total} matches played`} acts={by.past.length ? <button className="btn primary" onClick={() => setTab("past")}>See results</button> : undefined} />;
    if (active === "up") return <Empty icon={<IcCalL />} title="No upcoming matches" text={tournamentEnded ? "This tournament is over." : "All matches in this tournament have been played or are live now."} acts={by.past.length ? <button className="btn primary" onClick={() => setTab("past")}>See results</button> : undefined} />;
    return <Empty icon={<IcListL />} title="No results yet" text="Results appear here as soon as each match ends." acts={<button className="btn primary" onClick={() => setTab(by.live.length ? "live" : "up")}>See fixtures</button>} />;
  })();

  return (
    <main className="wrap main">
      {onSchedule && <ScheduleCard onSchedule={onSchedule} tournamentName={tournamentName} />}
      <div className="tool" ref={dd}>
        <div className="seg" role="tablist" aria-label="Match status">
          {BUCKETS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={active === k} onClick={() => { setTab(k); setQ(""); }}>
              {k === "live" && <span className={`ldot ${by.live.length ? "on" : "off"}`} />}{l}<span className={`ct ${by[k].length ? "" : "z"}`}>{by[k].length}</span>
            </button>
          ))}
        </div>
        <label className="search"><IcSearch /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search team, ground or match number" aria-label="Search matches" />{q && <button className="clr" onClick={() => setQ("")} aria-label="Clear search">×</button>}</label>
        <div className="dd">
          <button className={`ddb dateb ${range ? "set" : ""}`} aria-haspopup="dialog" aria-expanded={calOpen} onClick={(e) => { e.stopPropagation(); setTeamOpen(false); setCalOpen(!calOpen); }}><IcCal /><span className="lbl">{rangeLabel}</span><IcDn /></button>
          {calOpen && <Calendar days={days} counts={dayCounts} range={range} onPick={setRange} onClose={() => setCalOpen(false)} />}
        </div>
        <div className="dd">
          <button className="ddb" aria-haspopup="listbox" aria-expanded={teamOpen} onClick={(e) => { e.stopPropagation(); setCalOpen(false); setTeamOpen(!teamOpen); }}>
            {team === "all" ? <><IcTeams />All teams</> : <><Crest t={teams.find((t) => t.name === team) ?? teams[0]} size={20} />{team}</>}<IcDn />
          </button>
          {teamOpen && (
            <div className="ddm" role="listbox">
              <button className={team === "all" ? "on" : ""} onClick={() => { setTeam("all"); setTeamOpen(false); }}>All teams<span className="m">{inTab.length}</span></button><hr />
              {teams.map((t) => (<button key={t.name} role="option" className={team === t.name ? "on" : ""} onClick={() => { setTeam(t.name); setTeamOpen(false); }}><Crest t={t} />{t.name}<span className="m">{inTab.filter((m) => m.a.name === t.name || m.b.name === t.name).length}</span></button>))}
            </div>
          )}
        </div>
        <div className="view" role="group" aria-label="Layout">
          <button aria-pressed={view === "grid"} aria-label="Card view" onClick={() => setView("grid")}><IcGrid /></button>
          <button aria-pressed={view === "list"} aria-label="List view" onClick={() => setView("list")}><IcList /></button>
        </div>
      </div>

      {(filtered.length > 0 || anyFilter) && (
        <div className="days">
          <div className="summary" style={{ marginLeft: 0 }}>
            {filtered.length > 0 && <>Showing <b>{filtered.length}</b> {filtered.length === 1 ? "match" : "matches"}{range ? ` · ${rangeLabel}` : ""}{team !== "all" ? ` · ${team}` : ""}{active === "past" ? ", newest first" : ""}</>}
            {anyFilter && <button onClick={clear}>Clear filters</button>}
          </div>
          {view === "list" && active === "past" && <div className="legendwl"><span><b className="wl W">W</b>Won</span><span><b className="wl L">L</b>Lost</span><span><b className="wl T">T</b>Tied</span><span><b className="wl NR">NR</b>No result</span></div>}
        </div>
      )}

      <div id="list">
        {loading ? <div className="empty"><p>Loading matches…</p></div> : grouped.length === 0 ? emptyTab : grouped.map(({ k, list }) => {
          const grounds = [...new Set(list.map((m) => m.ground).filter(Boolean))];
          const rel = k === todayKey ? "Today · " : "";
          return (
            <div key={k}>
              <div className="day"><h3>{rel}{k === "tba" ? "Date to be announced" : dayLabelOf(parseKey(k))}</h3><span>{list.length} {list.length === 1 ? "match" : "matches"}{grounds.length ? ` · ${grounds.length > 2 ? `${grounds.length} grounds` : grounds.join(", ")}` : ""}</span></div>
              {view === "grid" ? <div className="grid">{list.map((m) => <MatchCard key={m.id} m={m} onOpen={open} />)}</div> : (
                <div className="tbl"><div className="th"><span>Match</span><span>Teams{active === "past" ? " · result" : ""}</span><span>{{ past: "Margin", live: "Situation", up: "Starts" }[active]}</span><span>{{ past: "Player of the match", live: "", up: "When" }[active]}</span><span>Ground</span><span /></div>{list.map((m) => <MatchRowItem key={m.id} m={m} onOpen={open} />)}</div>
              )}
            </div>
          );
        })}
      </div>
    </main>
  );
};
export default MatchesPanel;
